import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import RecurModalForm from "./RecurModalForm";
import { RecurCreate, RecurRestartArgs } from "../api/Person";
import {
  useCreateRecur,
  useRecurVerify,
  useRestartRecur,
} from "../api/PersonApi";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ReportsPendingReviewItem, ReportSummaryItemApi } from "../api/Reports";
import { useReportByUUID } from "../api/ReportsApi";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { Modal } from "../shared-components/Modal";
import { useEffect, useState } from "react";

type RecurCreateWrapperProps = {
  report?: ReportSummaryItemApi | ReportsPendingReviewItem;
  report_uuid?: string;
  recur_uuid?: string;
  isOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  onSuccess?: () => void;
};

const RecurCreateWrapper = ({
  report,
  report_uuid,
  recur_uuid,
  isOpen,
  setModalOpen,
  onSuccess,
}: RecurCreateWrapperProps) => {
  const { showLargeToast } = useApplicationToast();
  const { person } = usePermissionValidator();
  const { mutateAsync: createRecur } = useCreateRecur();
  const { mutateAsync: restartRecur } = useRestartRecur();
  const {
    mutate: reportMutate,
    isLoading: reportLoading,
    data: reportData,
  } = useReportByUUID();
  const {
    mutate: recurVerifyMutate,
    isLoading: recurVerifyLoading,
    isError: recurVerifyError,
  } = useRecurVerify();
  const [localReport, setLocalReport] = useState<
    ReportSummaryItemApi | ReportsPendingReviewItem | undefined
  >(undefined);
  const existing = !!recur_uuid;

  useEffect(() => {
    if (report) {
      setLocalReport(report);
    } else if (!!report_uuid) {
      reportMutate(report_uuid);
    }
    if (!!recur_uuid) {
      recurVerifyMutate({ recur_uuid: recur_uuid });
    }
  }, [
    report,
    report_uuid,
    recur_uuid,
    recurVerifyMutate,
    reportMutate,
    setLocalReport,
  ]);

  useEffect(() => {
    if (reportData) {
      setLocalReport(reportData);
    }
  }, [reportData]);

  const handleCreateRecur = async (data: RecurCreate) => {
    try {
      if (existing) {
        const transformed: RecurRestartArgs = {
          recur_uuid: recur_uuid ?? "",
          interval: Number(data.schedule_value),
          start_date: data.start_date?.toISOString(),
          run_now: data.run_now,
        };
        await restartRecur(transformed);
      } else {
        await createRecur(data);
      }
      showLargeToast?.({
        title: "Success! Employee Screening Scheduled.",
        message: "",
        icon: <CheckCircleIcon className="w-8 text-green-500" />,
        timeout: 3000,
        position: "top",
      });
    } catch (error) {
      showLargeToast?.({
        title: "Error scheduling Employee Screening.",
        message: "",
        icon: <ExclamationTriangleIcon className="w-8 text-red-500" />,
        timeout: 3000,
        position: "top",
      });
    }
    setModalOpen(false);
    onSuccess?.();
  };

  if (!person.canCreateRecur) {
    return <></>;
  }
  return (
    <>
      {reportLoading ||
      recurVerifyLoading ||
      recurVerifyError ||
      !localReport ? (
        <Modal
          open={isOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        >
          {recurVerifyError ? (
            <div className="flex gap-6">
              <div className="bg-red-100 text-red-600 rounded-full p-2 h-10">
                <ExclamationTriangleIcon className="w-6 h-6" />
              </div>
              <div>
                Cannot restart subscription due to the check type is no longer
                valid. Please rerun this candidate with a current check type and
                setup a new rescreening subscription.
              </div>
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </Modal>
      ) : (
        <RecurModalForm
          isOpen={isOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onSave={handleCreateRecur}
          report={localReport}
        />
      )}
    </>
  );
};

export default RecurCreateWrapper;
